import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import { Modal } from "../../../../shared/ui/modal";
import { useCenterCourses, useCenterMembers, useEnrollStudents, useSessionPreferences, academicYearOptions, type StudentBatchResult, type StudentInput } from "../../../center";
import {
    blankStudent,
    toStudentInput,
    validateStudent,
    type StudentPerson,
} from "../../domain/studentPerson";
import { PersonFields } from "../../../patient";
import { STUDENT_ERRORS } from "../refusals";
import CoupleFromPatientsForm from "./CoupleFromPatientsForm";
import GroupFromStudentsForm from "./GroupFromStudentsForm";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    centerId: string;
    /** A training centre enrols in a course; a therapy centre also makes couples. */
    isTraining: boolean;
}

type Mode = "individual" | "group" | "couple";

const SELECT_CLASS =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700";

interface Person extends StudentPerson {
    key: number;
}

let nextKey = 0;
const blankPerson = (): Person => ({ key: nextKey++, ...blankStudent() });

/**
 * A training centre's administrator takes students on.
 *
 * One student — assigned straight to a course and to a mentor, and the
 * centre's from the first day — or a group of students the centre already
 * has, as a therapist makes one. A spreadsheet has its own dialog.
 */
export default function AddStudentsModal({ isOpen, onClose, centerId, isTraining }: Props) {
    const { t } = useTranslation();
    const { courses } = useCenterCourses(centerId);
    const { members } = useCenterMembers(centerId, true);
    const enroll = useEnrollStudents(centerId);
    const mentors = members.filter((member) => member.roles.includes("therapist"));

    const [mode, setMode] = useState<Mode>("individual");
    // Adding one person is always offered: a student exists before any group
    // they join. A group or a couple only where the centre runs them.
    const { modes: sessionModes } = useSessionPreferences();
    const tabs = ((isTraining ? ["individual", "group"] : ["individual", "group", "couple"]) as Mode[]).filter(
        (value) => value === "individual" || sessionModes[value]
    );
    const [people, setPeople] = useState<Person[]>([blankPerson()]);
    const [courseId, setCourseId] = useState("");
    const [mentorEmail, setMentorEmail] = useState("");
    const [year, setYear] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [done, setDone] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setMode("individual");
        setPeople([blankPerson()]);
        setCourseId("");
        setMentorEmail("");
        setYear("");
        setSubmitted(false);
        setErrors([]);
        setDone(null);
    }, [isOpen]);

    const switchMode = (next: Mode) => {
        setMode(next);
        setSubmitted(false);
        setErrors([]);
        setDone(null);
        setPeople([blankPerson()]);
    };

    const changePerson = (key: number, changes: Partial<Person>) =>
        setPeople((current) => current.map((p) => (p.key === key ? { ...p, ...changes } : p)));

    // The patient form's own rules, person by person.
    const personErrors = (person: Person) => (submitted ? validateStudent(person) : {});
    const invalid =
        (isTraining && !courseId) ||
        !mentorEmail ||
        people.some((person) => Object.keys(validateStudent(person)).length > 0);

    const errorText = (code: string) => t(STUDENT_ERRORS[code] ?? "center.students.errors.generic");

    const handleSubmit = async () => {
        setSubmitted(true);
        setErrors([]);
        setDone(null);
        if (invalid) return;
        const rows: StudentInput[] = people.map((person) =>
            toStudentInput(person, {
                ...(isTraining ? { course_id: courseId, academic_year: year ? Number(year) : null } : {}),
                mentor_email: mentorEmail,
                group: null,
            })
        );
        try {
            const results: StudentBatchResult[] = await enroll.mutateAsync(rows);
            const refused = results.filter((result) => result.error);
            if (refused.length === 0) {
                setDone(t("center.students.created"));
                setPeople([blankPerson()]);
                setSubmitted(false);
                return;
            }
            setErrors(
                refused.map((result) => {
                    const person = people[result.index];
                    return `${person.first_name} ${person.last_name}: ${errorText(result.error as string)}`;
                })
            );
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
            setErrors([errorText(detail ?? "")]);
        }
    };

    const tab = (value: Mode) => (
        <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => switchMode(value)}
            className={`rounded-lg px-3 py-2 text-sm font-medium ${
                mode === value
                    ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/5"
            }`}
        >
            {t(`center.students.modes.${value}`)}
        </button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={t("center.students.title")}
            className="mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-surface-raised p-6"
        >
            <h3 className="text-lg font-semibold text-content-primary">{t("center.students.title")}</h3>
            <p className="mb-4 mt-1 text-sm text-content-tertiary">{t("center.students.subtitle")}</p>

            <div role="tablist" aria-label={t("center.students.title")} className="mb-5 flex flex-wrap gap-1">
                {tabs.map(tab)}
            </div>

            {mode === "group" ? (
                <GroupFromStudentsForm centerId={centerId} onClose={onClose} />
            ) : mode === "couple" ? (
                <CoupleFromPatientsForm centerId={centerId} onClose={onClose} />
            ) : (
                <form
                    noValidate
                    className="flex flex-col gap-5"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void handleSubmit();
                    }}
                >
                    <div className="grid gap-4 sm:grid-cols-2">
                        {isTraining && (
                        <div>
                            <Label htmlFor="student-course">{t("patient.course.course")}</Label>
                            <select
                                id="student-course"
                                value={courseId}
                                onChange={(e) => setCourseId(e.target.value)}
                                aria-invalid={submitted && !courseId}
                                className={`${SELECT_CLASS} ${submitted && !courseId ? "border-red-500" : ""}`}
                            >
                                <option value="">{t("center.students.chooseCourse")}</option>
                                {courses.map((course) => (
                                    <option key={course.id} value={course.id}>
                                        {course.internal_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        )}
                        <div>
                            <Label htmlFor="student-mentor">{t("center.students.mentor")}</Label>
                            <select
                                id="student-mentor"
                                value={mentorEmail}
                                onChange={(e) => setMentorEmail(e.target.value)}
                                aria-invalid={submitted && !mentorEmail}
                                className={`${SELECT_CLASS} ${submitted && !mentorEmail ? "border-red-500" : ""}`}
                            >
                                <option value="">{t("center.students.chooseMentor")}</option>
                                {mentors.map((mentor) => (
                                    <option key={mentor.therapist_id} value={mentor.email}>
                                        {`${mentor.first_name} ${mentor.last_name}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {isTraining && (
                        <div>
                            <Label htmlFor="student-year">{t("patient.course.year")}</Label>
                            <select
                                id="student-year"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                className={SELECT_CLASS}
                            >
                                <option value="">{t("patient.course.noYear")}</option>
                                {academicYearOptions().map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        )}
                    </div>

                    <fieldset className="flex flex-col gap-3">
                        <legend className="mb-1 text-sm font-semibold text-content-primary">
                            {t("center.students.student")}
                        </legend>
                        {people.map((person, index) => (
                            <div
                                key={person.key}
                                role="group"
                                aria-label={t("center.students.personN", { n: index + 1 })}
                                className="rounded-xl border border-gray-200 p-4 dark:border-gray-800"
                            >
                                <PersonFields
                                    idPrefix={`student-${person.key}`}
                                    value={person}
                                    onChange={(changes) => changePerson(person.key, changes)}
                                    errors={personErrors(person)}
                                />
                            </div>
                        ))}
                    </fieldset>

                    {submitted && invalid && (
                        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                            {t(isTraining ? "center.students.errorForm" : "center.students.errorFormNoCourse")}
                        </p>
                    )}
                    {errors.length > 0 && (
                        <ul role="alert" className="list-disc pl-5 text-sm text-red-600 dark:text-red-400">
                            {errors.map((message, index) => (
                                <li key={index}>{message}</li>
                            ))}
                        </ul>
                    )}
                    {done && (
                        <p role="status" className="text-sm text-brand-700 dark:text-brand-300">
                            {done}
                        </p>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={onClose}>
                            {t("common.close")}
                        </Button>
                        <Button type="submit" loading={enroll.isPending} disabled={enroll.isPending}>
                            {t("center.students.submit")}
                        </Button>
                    </div>
                </form>
            )}
        </Modal>
    );
}
